import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { StaticDatePicker } from '@mui/x-date-pickers'
import dayjs, { type Dayjs } from 'dayjs'
import { TimeWindowDtoUtil, type Timestamp, type TimeWindowDto } from '~/model'
import { isToday } from '~/helpers'
import { Section } from './Section'

type Props = {
  // data:
  selectedDateIso?: Timestamp
  timeWindows: TimeWindowDto[]
  // config:
  enableTimeWindows: boolean
  expanded: boolean
  // events:
  onToggleExpand: (expand: boolean) => void
  onSelectDate: (selectedDateIso: Timestamp) => void
}

export const DateSelector = ({
  selectedDateIso,
  enableTimeWindows,
  timeWindows,
  expanded,
  onToggleExpand,
  onSelectDate,
}: Props) => {
  const { t } = useTranslation()
  const valueLabel = selectedDateIso ? dayjs(selectedDateIso).format('D MMMM YYYY') : t('choose')

  const handleDateChange = (date: Dayjs | null) => {
    if (!isToday(date)) {
      onSelectDate(date!.toISOString())
    }
  }

  const shouldDisableDate = (date: Dayjs | null) => {
    if (!date) {
      return true
    }

    const dateIso = date.toISOString()
    const day = date.day()
    const startOfDayIso = dayjs().startOf('day').toISOString()
    const twoWeeksAheadIso = dayjs().add(2, 'weeks').toISOString()
    const isWorkingDay = day !== 6 && day !== 0
    const isInvalidTimeWindowDate = enableTimeWindows && !TimeWindowDtoUtil.containsDate(timeWindows, date)
    const isMoreThan2WeeksAhead = dateIso > twoWeeksAheadIso
    const isInPast = dateIso < startOfDayIso

    return enableTimeWindows
      ? !isToday(date) && (isInPast || isInvalidTimeWindowDate)
      : !isToday(date) && (isInPast || isMoreThan2WeeksAhead || !isWorkingDay)
  }

  return (
    <Section
      title={t('chooseDate')}
      valueLabel={valueLabel}
      isValid={!!selectedDateIso}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      sm={false}
    >
      <Box
        sx={({ palette: { primary, common } }) => ({
          marginTop: 1,
          '& .MuiPickersDay-current': {
            color: `${primary.main} !important`,
          },
          '& .MuiPickersDay-current.MuiPickersDay-daySelected': {
            color: `${common.white} !important`,
          },
        })}
      >
        <StaticDatePicker
          shouldDisableDate={shouldDisableDate}
          openTo="day"
          value={dayjs(selectedDateIso)}
          onChange={handleDateChange}
          slotProps={{
            toolbar: {
              hidden: true,
            },
          }}
        />
      </Box>
    </Section>
  )
}
